import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Smartphone, Play, Pause, Image as ImageIcon, Volume2, VolumeX, Move } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useFramesStore from '../../../stores/framesStore';
import useSyncedMedia from '../../classroom/useSyncedMedia';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

const CORNER_KEYS = ['tl', 'tr', 'br', 'bl'];

// Media pan/zoom bounds — scale 1 = cover, up to 5x; offsets clamp to the
// range that keeps the (scaled) media covering the slot (no reveal of empty
// background outside the media edges).
const MEDIA_SCALE_MIN = 1;
const MEDIA_SCALE_MAX = 5;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const maxOffsetPct = (scale) => Math.max(0, (scale - 1) * 50);
const clampMediaTransform = ({ scale, offsetX, offsetY }) => {
  const s = clamp(Number.isFinite(scale) ? scale : 1, MEDIA_SCALE_MIN, MEDIA_SCALE_MAX);
  const maxOff = maxOffsetPct(s);
  return {
    scale: s,
    offsetX: clamp(Number.isFinite(offsetX) ? offsetX : 0, -maxOff, maxOff),
    offsetY: clamp(Number.isFinite(offsetY) ? offsetY : 0, -maxOff, maxOff),
  };
};

const hasValidCorners = (corners) => {
  if (!corners || typeof corners !== 'object') return false;
  return CORNER_KEYS.every((k) => {
    const c = corners[k];
    return c && Number.isFinite(Number(c.x)) && Number.isFinite(Number(c.y));
  });
};

// Heckbert's closed-form solution: maps the unit square
// (0,0)→(x0,y0), (1,0)→(x1,y1), (1,1)→(x2,y2), (0,1)→(x3,y3) into a quad.
// Returns 8 projective coefficients.
const unitSquareToQuad = (x0, y0, x1, y1, x2, y2, x3, y3) => {
  const dx1 = x1 - x2;
  const dx2 = x3 - x2;
  const dx3 = x0 - x1 + x2 - x3;
  const dy1 = y1 - y2;
  const dy2 = y3 - y2;
  const dy3 = y0 - y1 + y2 - y3;

  if (dx3 === 0 && dy3 === 0) {
    return {
      a: x1 - x0, b: x2 - x1, c: x0,
      d: y1 - y0, e: y2 - y1, f: y0,
      g: 0, h: 0
    };
  }
  const denom = dx1 * dy2 - dy1 * dx2;
  if (denom === 0) return null;
  const g = (dx3 * dy2 - dy3 * dx2) / denom;
  const h = (dx1 * dy3 - dy1 * dx3) / denom;
  return {
    a: x1 - x0 + g * x1,
    b: x3 - x0 + h * x3,
    c: x0,
    d: y1 - y0 + g * y1,
    e: y3 - y0 + h * y3,
    f: y0,
    g, h
  };
};

// Corners are authored in the admin as % of the frame image's own box. On a
// slide, the frame image is rendered with `object-contain` inside a container
// whose aspect ratio may differ, producing letterbox/pillarbox bands. This
// remaps corner % (image-space) → corner % (container-space) so the perspective
// quad tracks the drawn image regardless of container aspect.
const remapCornersToContainer = (corners, containerW, containerH, imgAspect) => {
  if (
    !hasValidCorners(corners) ||
    !imgAspect ||
    !Number.isFinite(imgAspect) ||
    containerW <= 0 ||
    containerH <= 0
  ) {
    return corners;
  }
  const containerAspect = containerW / containerH;
  let imgW, imgH, offsetX, offsetY;
  if (imgAspect > containerAspect) {
    // Pillarbox: image spans full width, bands top/bottom.
    imgW = containerW;
    imgH = containerW / imgAspect;
    offsetX = 0;
    offsetY = (containerH - imgH) / 2;
  } else {
    // Letterbox: image spans full height, bands left/right.
    imgH = containerH;
    imgW = containerH * imgAspect;
    offsetX = (containerW - imgW) / 2;
    offsetY = 0;
  }
  const remap = (c) => ({
    x: ((offsetX + (Number(c.x) / 100) * imgW) / containerW) * 100,
    y: ((offsetY + (Number(c.y) / 100) * imgH) / containerH) * 100
  });
  return {
    tl: remap(corners.tl),
    tr: remap(corners.tr),
    br: remap(corners.br),
    bl: remap(corners.bl)
  };
};

// Build CSS matrix3d that maps a source rect (0,0)-(W,0)-(W,H)-(0,H)
// onto the destination quad given in percent-of-container coordinates.
const buildMatrix3d = (corners, containerW, containerH) => {
  if (!hasValidCorners(corners) || containerW <= 0 || containerH <= 0) return null;
  const px = (c) => (Number(c.x) / 100) * containerW;
  const py = (c) => (Number(c.y) / 100) * containerH;
  const H = unitSquareToQuad(
    px(corners.tl), py(corners.tl),
    px(corners.tr), py(corners.tr),
    px(corners.br), py(corners.br),
    px(corners.bl), py(corners.bl)
  );
  if (!H) return null;
  // Compose with source scale (source is W x H, unit-square math wants 1 x 1).
  const a = H.a / containerW;
  const b = H.b / containerH;
  const c = H.c;
  const d = H.d / containerW;
  const e = H.e / containerH;
  const f = H.f;
  const g = H.g / containerW;
  const h = H.h / containerH;
  // matrix3d is column-major: col1(a,d,0,g), col2(b,e,0,h), col3(0,0,1,0), col4(c,f,0,1)
  return `matrix3d(${a},${d},0,${g},${b},${e},0,${h},0,0,1,0,${c},${f},0,1)`;
};

// Renders a decorative frame (e.g. phone) with a media slot (image or video)
// clipped to the frame's transparent "screen" area.
//
// The frame image sits on top with pointer-events:none, so the transparent
// screen area of the SVG shows the underlying media through. The media is
// absolutely positioned within a fixed rectangle defined by the frame's
// screen_x / screen_y / screen_width / screen_height / screen_radius fields.
//
// Modes:
//   editor    — video is not autoplayed; a big play/pause overlay toggles it.
//   view      — lesson/preview view. When a lessonSessionId is provided the
//               video is synced between teacher and student (teacher controls,
//               student mirrors); otherwise falls back to native controls.
//   thumbnail — static poster; video renders first frame only.
const FrameElement = ({
  element,
  mode = 'editor',
  isTeacher = false,
  lessonSessionId = null,
  isAdjusting = false,
  onMediaTransformChange = null,
}) => {
  const { t } = useTranslation();
  const content = element.content || {};

  const mediaTransform = clampMediaTransform({
    scale: content.mediaScale,
    offsetX: content.mediaOffsetX,
    offsetY: content.mediaOffsetY,
  });
  const mediaTransformCss = `translate(${mediaTransform.offsetX}%, ${mediaTransform.offsetY}%) scale(${mediaTransform.scale})`;

  // Trim support — bounded playback window inside the uploaded video. trimStart
  // defaults to 0; trimEnd null means "to the end" (we don't know duration until
  // metadata loads). Enforced via onLoadedMetadata (seek) + onTimeUpdate
  // (loop/pause). The scrubber shown to the teacher clamps to this window.
  const trimStart = Math.max(0, Number(content.trimStart) || 0);
  const trimEndRaw = Number(content.trimEnd);
  const trimEnd = Number.isFinite(trimEndRaw) && trimEndRaw > trimStart ? trimEndRaw : null;

  // Resolve live frame data from the shared store so admin edits (corners,
  // screen, image) reach already-placed slide elements without a manual
  // refresh. Falls back to whatever the element has baked in for pre-store
  // elements and while the initial fetch is in flight.
  const fetchIfNeeded = useFramesStore((s) => s.fetchIfNeeded);
  useEffect(() => { fetchIfNeeded(); }, [fetchIfNeeded]);
  const storeFrame = useFramesStore((s) => s.resolveFrame(content.frameId, content.frameSrc));

  const frameSrc = storeFrame?.file_path ?? content.frameSrc;
  const corners = storeFrame?.screen_corners ?? content.corners;
  const screen = storeFrame
    ? {
        x: Number(storeFrame.screen_x),
        y: Number(storeFrame.screen_y),
        width: Number(storeFrame.screen_width),
        height: Number(storeFrame.screen_height),
        radius: Number(storeFrame.screen_radius),
      }
    : (content.screen || { x: 0, y: 0, width: 100, height: 100, radius: 0 });

  const perspectiveMode = hasValidCorners(corners);
  const mediaSrc = content.mediaSrc;
  const mediaType = content.mediaType;

  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(!!content.muted);

  // Teacher⇄student sync for view mode. Hook is safe to call unconditionally —
  // it no-ops when isTeacher/lessonSessionId are falsy. We render our own
  // play/pause overlay (native controls don't survive the perspective transform)
  // and rely on the video's onPlay/onPause events firing when the button
  // programmatically toggles playback — that's what pushes state to the server.
  const syncEnabled = mode === 'view' && !!lessonSessionId;
  const { mediaRef: syncedVideoRef, teacherProps } = useSyncedMedia({
    elementId: element.id,
    isTeacher,
    lessonSessionId,
  });

  // Measure container size for perspective transform (matrix3d needs pixel space).
  // Use offsetWidth/Height (layout size) rather than getBoundingClientRect, which
  // returns the painted size — in SlideThumbnail the whole slide is wrapped in
  // `transform: scale(...)` and starts at scale 0, so getBoundingClientRect would
  // read 0×0 and ResizeObserver never fires on ancestor transform changes, leaving
  // matrix3d null and the media wrapper permanently hidden in thumbnails.
  const containerRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    if (!perspectiveMode) return;
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      setContainerSize({ w: el.offsetWidth, h: el.offsetHeight });
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [perspectiveMode]);

  // Frame image's natural aspect ratio — needed to convert corner %
  // (image-space) into container % once object-contain letterboxes the image.
  // The store's file_path is authoritative (admin may have re-uploaded), so
  // measure it fresh. Baked originalWidth/Height is only trusted as a
  // synchronous fallback when the store hasn't resolved yet (avoids a
  // one-frame flash while the image loads).
  const bakedAspect =
    !storeFrame &&
    Number(content.originalWidth) > 0 &&
    Number(content.originalHeight) > 0
      ? Number(content.originalWidth) / Number(content.originalHeight)
      : null;
  const [measuredAspect, setMeasuredAspect] = useState(null);
  useEffect(() => {
    if (!perspectiveMode || !frameSrc) return;
    setMeasuredAspect(null);
    const img = new window.Image();
    img.onload = () => {
      if (img.naturalWidth > 0 && img.naturalHeight > 0) {
        setMeasuredAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = getMediaUrl(frameSrc);
  }, [perspectiveMode, frameSrc]);
  const imgAspect = measuredAspect || bakedAspect || null;

  useEffect(() => {
    if (mode !== 'view' || syncEnabled) return;
    if (mediaType !== 'video' || !videoRef.current || !content.autoplay) return;
    const v = videoRef.current;
    v.muted = true; // autoplay requires muted in most browsers
    try { if (trimStart > 0) v.currentTime = trimStart; } catch {}
    v.play().catch(() => {});
  }, [mode, syncEnabled, mediaType, content.autoplay, mediaSrc, trimStart]);

  // Snap playhead into the trim window — used on load, on play, and on scrub.
  const applyTrimBounds = useCallback((v) => {
    if (!v) return;
    const endBound = trimEnd ?? v.duration ?? 0;
    if (v.currentTime < trimStart - 0.01 || (endBound > 0 && v.currentTime >= endBound - 0.01)) {
      try { v.currentTime = trimStart; } catch {}
    }
  }, [trimStart, trimEnd]);

  // Called on every timeupdate — enforces the trim-end boundary by either
  // looping back to trimStart (if content.loop) or pausing at trimEnd.
  const enforceTrimEnd = useCallback((v) => {
    if (!v) return;
    const endBound = trimEnd ?? v.duration ?? 0;
    if (endBound <= 0) return;
    if (v.currentTime >= endBound - 0.03) {
      if (content.loop) {
        try { v.currentTime = trimStart; } catch {}
      } else {
        v.pause();
        try { v.currentTime = endBound; } catch {}
      }
    }
  }, [trimStart, trimEnd, content.loop]);

  const toggleVideo = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = syncEnabled ? syncedVideoRef.current : videoRef.current;
    if (!v) return;
    if (v.paused) {
      applyTrimBounds(v);
      v.play().then(() => setIsPlaying(true)).catch(() => {});
    } else {
      v.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const v = syncEnabled ? syncedVideoRef.current : videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setIsMuted(next);
  };

  const handleScrub = (e) => {
    const v = syncEnabled ? syncedVideoRef.current : videoRef.current;
    if (!v) return;
    const raw = parseFloat(e.target.value);
    if (!Number.isFinite(raw)) return;
    const endBound = trimEnd ?? v.duration ?? raw;
    const next = Math.min(endBound, Math.max(trimStart, raw));
    v.currentTime = next;
    setCurrentTime(next);
    // The video's onSeeked handler (teacher branch) pushes state to the
    // server, so the student mirrors the seek automatically.
  };

  const formatTime = (seconds) => {
    const s = Math.max(0, Math.floor(seconds || 0));
    const mm = Math.floor(s / 60);
    const ss = s % 60;
    return `${mm}:${ss.toString().padStart(2, '0')}`;
  };

  const rectStyle = {
    position: 'absolute',
    left: `${screen.x}%`,
    top: `${screen.y}%`,
    width: `${screen.width}%`,
    height: `${screen.height}%`,
    borderRadius: `${screen.radius}%`,
    overflow: 'hidden',
    backgroundColor: mediaSrc ? 'transparent' : '#f3f4f6'
  };

  const adjustedCorners = perspectiveMode
    ? remapCornersToContainer(corners, containerSize.w, containerSize.h, imgAspect)
    : corners;

  const matrix = perspectiveMode
    ? buildMatrix3d(adjustedCorners, containerSize.w, containerSize.h)
    : null;

  // Perspective-warped wrapper for the media. Sized 100%×100%; matrix3d then
  // maps its four corners onto the destination quad in pixel space.
  const perspectiveMediaStyle = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    transformOrigin: '0 0',
    transform: matrix || 'none',
    // Hide the transform until we've measured the container, to avoid a
    // one-frame flash where the identity transform shows the media untransformed.
    visibility: matrix ? 'visible' : 'hidden',
    overflow: 'hidden'
  };

  // Empty-state placeholder inside the quad, kept upright (not warped).
  const cornersPolygon = perspectiveMode
    ? `polygon(${adjustedCorners.tl.x}% ${adjustedCorners.tl.y}%, ${adjustedCorners.tr.x}% ${adjustedCorners.tr.y}%, ${adjustedCorners.br.x}% ${adjustedCorners.br.y}%, ${adjustedCorners.bl.x}% ${adjustedCorners.bl.y}%)`
    : null;
  const emptyPlaceholderStyle = perspectiveMode
    ? {
        position: 'absolute',
        inset: 0,
        backgroundColor: '#f3f4f6',
        clipPath: cornersPolygon,
        WebkitClipPath: cornersPolygon
      }
    : rectStyle;

  const mediaWrapperStyle = perspectiveMode ? perspectiveMediaStyle : rectStyle;

  // CSS transform applied to the img/video so the media can be panned and
  // zoomed inside the frame's slot (Canva-style crop). The outer wrapper has
  // `overflow: hidden`, so scaling >1 reveals a cropped view that the user
  // can shift with offsetX/offsetY.
  const mediaInnerStyle = {
    width: '100%',
    height: '100%',
    transform: mediaTransformCss,
    transformOrigin: 'center',
  };

  // Pan & zoom interaction (editor + isAdjusting). We track the pan drag on a
  // ref so React re-renders don't fight the pointer stream, and only flush
  // the final value to the store on pointer up (intermediate updates still
  // go through onMediaTransformChange so the user sees live motion).
  const adjustRectRef = useRef(null);
  const panStateRef = useRef(null);
  const emitTransform = useCallback((next) => {
    if (!onMediaTransformChange) return;
    const clamped = clampMediaTransform(next);
    onMediaTransformChange({
      mediaScale: clamped.scale,
      mediaOffsetX: clamped.offsetX,
      mediaOffsetY: clamped.offsetY,
    });
  }, [onMediaTransformChange]);

  const handleAdjustPointerDown = (e) => {
    if (!isAdjusting || mode !== 'editor') return;
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = adjustRectRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    try { e.currentTarget?.setPointerCapture?.(e.pointerId); } catch {}
    panStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: mediaTransform.offsetX,
      startOffsetY: mediaTransform.offsetY,
      rectW: rect.width,
      rectH: rect.height,
      scale: mediaTransform.scale,
    };
  };

  const handleAdjustPointerMove = (e) => {
    const pan = panStateRef.current;
    if (!pan) return;
    e.preventDefault();
    e.stopPropagation();
    const dxPct = ((e.clientX - pan.startX) / pan.rectW) * 100;
    const dyPct = ((e.clientY - pan.startY) / pan.rectH) * 100;
    emitTransform({
      scale: pan.scale,
      offsetX: pan.startOffsetX + dxPct,
      offsetY: pan.startOffsetY + dyPct,
    });
  };

  const handleAdjustPointerUp = (e) => {
    if (!panStateRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    try { e.currentTarget?.releasePointerCapture?.(e.pointerId); } catch {}
    panStateRef.current = null;
  };

  const handleAdjustWheel = (e) => {
    if (!isAdjusting || mode !== 'editor') return;
    e.preventDefault();
    e.stopPropagation();
    const step = e.deltaY < 0 ? 0.1 : -0.1;
    const nextScale = clamp(mediaTransform.scale + step, MEDIA_SCALE_MIN, MEDIA_SCALE_MAX);
    if (nextScale === mediaTransform.scale) return;
    emitTransform({
      scale: nextScale,
      offsetX: mediaTransform.offsetX,
      offsetY: mediaTransform.offsetY,
    });
  };

  // Wheel listener must be non-passive so preventDefault() works and the
  // whole page doesn't scroll under the user. React's onWheel is passive by
  // default on some browsers, so attach manually via useEffect.
  useEffect(() => {
    if (!isAdjusting || mode !== 'editor') return;
    const el = adjustRectRef.current;
    if (!el) return;
    const handler = (ev) => handleAdjustWheel(ev);
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdjusting, mode, mediaTransform.scale, mediaTransform.offsetX, mediaTransform.offsetY]);

  return (
    <div ref={containerRef} className="w-full h-full relative">
      {/* Placeholder (empty state) — kept upright even in perspective mode */}
      {!mediaSrc && mode !== 'thumbnail' && (
        <div style={emptyPlaceholderStyle}>
          {!perspectiveMode && (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 gap-1">
              <ImageIcon size={20} />
              <span className="text-[10px] text-center px-2">{t('games.frame.addPhotoOrVideo')}</span>
            </div>
          )}
        </div>
      )}

      {/* Media slot */}
      {mediaSrc && (
        <div style={mediaWrapperStyle}>
          <div style={mediaInnerStyle}>
            {mediaType === 'image' && (
              <img
                src={getMediaUrl(mediaSrc)}
                alt={content.fileName || 'Frame media'}
                className="w-full h-full object-cover pointer-events-none select-none"
                draggable={false}
              />
            )}

            {mediaType === 'video' && mode === 'view' && syncEnabled && (
              <video
                ref={syncedVideoRef}
                src={getMediaUrl(mediaSrc)}
                className="w-full h-full object-cover pointer-events-none"
                playsInline
                loop={content.loop}
                muted={isMuted}
                autoPlay={isTeacher && !!content.autoplay}
                onPlay={(e) => { setIsPlaying(true); teacherProps.onPlay?.(e); }}
                onPause={(e) => { setIsPlaying(false); teacherProps.onPause?.(e); }}
                onSeeked={(e) => { teacherProps.onSeeked?.(e); }}
                onEnded={() => setIsPlaying(false)}
                onLoadedMetadata={(e) => {
                  if (isTeacher) setDuration(e.currentTarget.duration || 0);
                  applyTrimBounds(e.currentTarget);
                }}
                onTimeUpdate={(e) => {
                  if (isTeacher) setCurrentTime(e.currentTarget.currentTime || 0);
                  enforceTrimEnd(e.currentTarget);
                }}
                onContextMenu={(e) => e.preventDefault()}
              />
            )}

            {mediaType === 'video' && mode === 'view' && !syncEnabled && (
              <video
                ref={videoRef}
                src={getMediaUrl(mediaSrc)}
                className="w-full h-full object-cover"
                controls
                controlsList="nodownload"
                loop={content.loop}
                muted={content.muted}
                playsInline
                onLoadedMetadata={(e) => applyTrimBounds(e.currentTarget)}
                onTimeUpdate={(e) => enforceTrimEnd(e.currentTarget)}
              />
            )}

            {mediaType === 'video' && mode === 'editor' && (
              <>
                <video
                  ref={videoRef}
                  src={getMediaUrl(mediaSrc)}
                  className="w-full h-full object-cover pointer-events-none"
                  muted
                  loop={content.loop}
                  onEnded={() => setIsPlaying(false)}
                  onLoadedMetadata={(e) => applyTrimBounds(e.currentTarget)}
                  onTimeUpdate={(e) => enforceTrimEnd(e.currentTarget)}
                />
                {!isAdjusting && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <button
                      onMouseDown={toggleVideo}
                      className="w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition pointer-events-auto"
                    >
                      {isPlaying ? (
                        <Pause size={18} className="text-white" />
                      ) : (
                        <Play size={18} className="text-white ml-0.5" />
                      )}
                    </button>
                  </div>
                )}
              </>
            )}

            {mediaType === 'video' && mode === 'thumbnail' && (
              <video
                src={`${getMediaUrl(mediaSrc)}#t=${trimStart > 0 ? trimStart : 0.1}`}
                className="w-full h-full object-cover"
                muted
                playsInline
                preload="metadata"
              />
            )}
          </div>

          {/* Adjust-mode capture layer — eats pointer events on the media so
              drag pans and wheel zooms the media inside the frame slot.
              Rendered inside the perspective wrapper so pointer hits land on
              the warped quad area (not the full element bounding box). */}
          {isAdjusting && mode === 'editor' && (
            <div
              ref={adjustRectRef}
              className="absolute inset-0 cursor-move"
              style={{
                background: 'linear-gradient(0deg, rgba(42,77,37,0.08), rgba(42,77,37,0.08))',
                boxShadow: 'inset 0 0 0 2px rgba(42,77,37,0.9)',
                touchAction: 'none',
              }}
              onPointerDown={handleAdjustPointerDown}
              onPointerMove={handleAdjustPointerMove}
              onPointerUp={handleAdjustPointerUp}
              onPointerCancel={handleAdjustPointerUp}
              onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
            >
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="flex items-center gap-1.5 rounded-full bg-black/60 px-2.5 py-1 text-[11px] text-white shadow">
                  <Move size={12} />
                  <span>{t('editor.properties.frameMediaAdjustHint')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Teacher-only control bar for synced view mode: play/pause + scrubber
          + time. Positioned outside the perspective wrapper so it stays
          upright and clickable regardless of the media's 3D transform.
          Student never sees this — their playback mirrors the teacher via
          useSyncedMedia. */}
      {mediaSrc && mediaType === 'video' && mode === 'view' && syncEnabled && isTeacher && (
        <div
          className="absolute flex items-center gap-2 rounded-full bg-black/60 backdrop-blur-sm px-3 py-1.5 pointer-events-auto shadow-lg"
          style={
            perspectiveMode && adjustedCorners
              ? {
                  left: `${(adjustedCorners.bl.x + adjustedCorners.br.x) / 2}%`,
                  top: `${(adjustedCorners.bl.y + adjustedCorners.br.y) / 2}%`,
                  transform: 'translate(-50%, -100%) translateY(-8px)',
                }
              : {
                  left: `${screen.x + screen.width / 2}%`,
                  top: `${screen.y + screen.height}%`,
                  transform: 'translate(-50%, -100%) translateY(-8px)',
                }
          }
          onMouseDown={(e) => e.stopPropagation()}
        >
          <button
            onMouseDown={toggleVideo}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition flex-shrink-0"
          >
            {isPlaying ? (
              <Pause size={14} className="text-white" />
            ) : (
              <Play size={14} className="text-white ml-0.5" />
            )}
          </button>
          {(() => {
            const scrubMax = trimEnd ?? duration ?? 0;
            const scrubMin = trimStart;
            const trimDuration = Math.max(0, scrubMax - scrubMin);
            const trimElapsed = Math.max(0, Math.min(trimDuration, (currentTime || 0) - scrubMin));
            return (
              <>
                <span className="text-[11px] text-white tabular-nums min-w-[32px] text-right">
                  {formatTime(trimElapsed)}
                </span>
                <input
                  type="range"
                  min={scrubMin}
                  max={scrubMax}
                  step={0.1}
                  value={Math.min(Math.max(currentTime, scrubMin), scrubMax)}
                  onChange={handleScrub}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="w-20 h-1 accent-white cursor-pointer"
                  disabled={!duration}
                />
                <span className="text-[11px] text-white tabular-nums min-w-[32px]">
                  {formatTime(trimDuration)}
                </span>
              </>
            );
          })()}
          <button
            onMouseDown={toggleMute}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition flex-shrink-0"
            title={isMuted ? 'Увімкнути звук' : 'Вимкнути звук'}
          >
            {isMuted ? (
              <VolumeX size={14} className="text-white" />
            ) : (
              <Volume2 size={14} className="text-white" />
            )}
          </button>
        </div>
      )}

      {/* Empty-state placeholder icon (upright, centered on quad centroid) */}
      {!mediaSrc && mode !== 'thumbnail' && perspectiveMode && (
        <div
          className="absolute flex flex-col items-center justify-center text-gray-400 gap-1 pointer-events-none"
          style={{
            left: `${(adjustedCorners.tl.x + adjustedCorners.tr.x + adjustedCorners.br.x + adjustedCorners.bl.x) / 4}%`,
            top: `${(adjustedCorners.tl.y + adjustedCorners.tr.y + adjustedCorners.br.y + adjustedCorners.bl.y) / 4}%`,
            transform: 'translate(-50%, -50%)'
          }}
        >
          <ImageIcon size={20} />
          <span className="text-[10px] text-center px-2">{t('games.frame.addPhotoOrVideo')}</span>
        </div>
      )}

      {/* Frame image on top */}
      {frameSrc ? (
        <img
          src={getMediaUrl(frameSrc)}
          alt=""
          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
          draggable={false}
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Smartphone size={24} className="text-gray-400" />
        </div>
      )}
    </div>
  );
};

export default FrameElement;
